/**
 * MSAL Configuration for Azure Entra ID Authentication
 *
 * Uses runtime config from /config.json (loaded in index.tsx before
 * this module is consumed). Falls back to build-time env vars.
 */
import { Configuration, LogLevel } from "@azure/msal-browser";

import { getConfig } from "../config";

/**
 * Build the MSAL configuration object from runtime config.
 * Called after loadConfig() has completed in index.tsx.
 */
export function buildMsalConfig(): Configuration {
  const cfg = getConfig();
  return {
    auth: {
      clientId: cfg.clientId,
      authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
      redirectUri: cfg.redirectUri,
      postLogoutRedirectUri: cfg.redirectUri,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: "sessionStorage",
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              break;
            case LogLevel.Warning:
              console.warn(message);
              break;
            case LogLevel.Info:
              break;
            case LogLevel.Verbose:
              break;
          }
        },
        logLevel: LogLevel.Warning,
      },
    },
  };
}

/**
 * Scopes for ID token (login)
 */
export const loginRequest = {
  scopes: ["openid", "profile", "email"],
};

/**
 * Scopes for access token (API calls)
 */
export function getApiRequest() {
  const cfg = getConfig();
  return {
    scopes: [`api://${cfg.clientId}/access_as_user`],
  };
}

/**
 * App Roles defined in Entra ID
 */
export const AppRoles = {
  PORTAL_USER: "Portal.User",
  PORTAL_ADMIN: "Portal.Admin",
  TOOL_INVENTORY: "Tool.Inventory",
  TOOL_ANALYTICS: "Tool.Analytics",
  TOOL_AI_ASSISTANT: "Tool.AIAssistant",
} as const;

export type AppRole = (typeof AppRoles)[keyof typeof AppRoles];
